/**
 * PinSheet — Set / verify a 4-digit payment PIN. Used to gate withdrawals.
 * Modes:
 *   "set"      → set new PIN (with confirm)
 *   "verify"   → user must enter their existing PIN
 *   "reset"    → forgot PIN → mock OTP → new PIN
 *
 * Calls onSuccess({ pin }) on completion.
 */
import { useEffect, useState } from "react";
import { X, ShieldCheck, Loader2, KeyRound } from "lucide-react";
import {
    hasPaymentPin,
    setPaymentPin,
    verifyPaymentPin,
    resetPaymentPin,
} from "@/lib/monetizationStore";

const Pad = ({ value, onChange, length = 4, testId }) => {
    const handleKey = (k) => {
        if (k === "del") onChange(value.slice(0, -1));
        else if (value.length < length) onChange(value + k);
    };
    const keys = ["1","2","3","4","5","6","7","8","9","","0","del"];
    return (
        <div data-testid={testId}>
            {/* Display dots */}
            <div className="flex justify-center gap-3 mb-5">
                {Array.from({ length }).map((_, i) => (
                    <span
                        key={i}
                        className={[
                            "h-3 w-3 rounded-full border-2",
                            i < value.length ? "bg-primary border-primary" : "border-muted-foreground/40",
                        ].join(" ")}
                    />
                ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
                {keys.map((k, i) => k === "" ? (
                    <span key={i} />
                ) : (
                    <button
                        key={i}
                        type="button"
                        data-testid={`pin-key-${k}`}
                        onClick={() => handleKey(k)}
                        className="h-12 rounded-2xl bg-secondary text-foreground font-display text-[18px] font-semibold hover:bg-muted active:scale-[0.97]"
                    >
                        {k === "del" ? "←" : k}
                    </button>
                ))}
            </div>
        </div>
    );
};

const PinSheet = ({ open, onClose, userId, mode = "verify", onSuccess, title, subtitle }) => {
    // Internal step machine for set/reset (entry → confirm)
    const [stage, setStage] = useState("entry"); // entry | confirm | otp | newPin | confirmReset | working
    const [first, setFirst] = useState("");
    const [second, setSecond] = useState("");
    const [otp, setOtp] = useState("");
    const [generatedOtp, setGeneratedOtp] = useState("");
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!open) return;
        setFirst(""); setSecond(""); setOtp(""); setErr(null);
        if (mode === "set") setStage("entry");
        else if (mode === "reset") {
            const code = String(Math.floor(100000 + Math.random() * 900000));
            setGeneratedOtp(code);
            setStage("otp");
        } else setStage("entry");
    }, [open, mode]);

    if (!open) return null;

    const PIN_LEN = 4;

    const submit = () => {
        setErr(null);
        if (mode === "verify") {
            const r = verifyPaymentPin(userId, first);
            if (!r.ok) { setErr(r.error || "Wrong PIN"); setFirst(""); return; }
            setStage("working");
            onSuccess?.({ pin: first });
            return;
        }
        if (mode === "set") {
            if (stage === "entry") {
                if (first.length !== PIN_LEN) { setErr("Enter a 4-digit PIN"); return; }
                setStage("confirm");
                return;
            }
            if (stage === "confirm") {
                if (second !== first) { setErr("PINs don't match"); setSecond(""); return; }
                const r = setPaymentPin(userId, first);
                if (!r.ok) { setErr(r.error); return; }
                setStage("working");
                onSuccess?.({ pin: first });
                return;
            }
        }
        if (mode === "reset") {
            if (stage === "otp") {
                if (otp !== generatedOtp) { setErr("Wrong OTP. Use the code shown above."); setOtp(""); return; }
                setStage("newPin");
                return;
            }
            if (stage === "newPin") {
                if (first.length !== PIN_LEN) { setErr("Enter a 4-digit PIN"); return; }
                setStage("confirmReset");
                return;
            }
            if (stage === "confirmReset") {
                if (second !== first) { setErr("PINs don't match"); setSecond(""); return; }
                resetPaymentPin(userId, first);
                setStage("working");
                onSuccess?.({ pin: first });
                return;
            }
        }
    };

    const headerTitle =
        title ||
        (mode === "verify" ? "Enter your PIN"
            : mode === "set" ? "Set a payment PIN"
            : "Reset payment PIN");

    const stageSubtitle =
        mode === "verify" ? "Enter your 4-digit PIN to confirm"
        : mode === "set" && stage === "entry" ? "Choose a 4-digit PIN — used to authorise withdrawals."
        : mode === "set" && stage === "confirm" ? "Re-enter your new PIN to confirm."
        : mode === "reset" && stage === "otp" ? `Enter the 6-digit code we sent. (Code: ${generatedOtp})`
        : mode === "reset" && stage === "newPin" ? "Choose a new 4-digit PIN."
        : mode === "reset" && stage === "confirmReset" ? "Re-enter your new PIN."
        : subtitle || "";

    const value = stage === "confirm" || stage === "confirmReset" ? second : first;
    const setValue = stage === "confirm" || stage === "confirmReset" ? setSecond : setFirst;

    const canSubmit =
        stage === "otp" ? otp.length === 6 :
        value.length === PIN_LEN;

    const showForgot = mode === "verify" && hasPaymentPin(userId);

    return (
        <div className="absolute inset-0 z-50 flex items-end" data-testid="pin-sheet" role="dialog" aria-modal="true">
            <button
                onClick={() => stage !== "working" && onClose?.()}
                aria-label="Close"
                className="absolute inset-0 bg-black/55 animate-fade-in"
                data-testid="pin-sheet-scrim"
            />
            <div className="relative z-10 w-full bg-card border-t border-border rounded-t-[28px] shadow-[0_-20px_48px_-12px_rgba(0,0,0,0.4)] animate-slide-up pb-[max(20px,env(safe-area-inset-bottom))]">
                <div className="flex justify-center pt-2.5 pb-1">
                    <span className="h-1.5 w-10 rounded-full bg-muted" />
                </div>
                <div className="px-5 pt-1.5 pb-3 flex items-center justify-between border-b border-border/60">
                    <div className="min-w-0">
                        <h2 className="font-display text-[16px] font-semibold leading-tight inline-flex items-center gap-1.5">
                            <ShieldCheck size={14} className="text-primary" />
                            {headerTitle}
                        </h2>
                        <p className="text-[11.5px] text-muted-foreground mt-0.5">{stageSubtitle}</p>
                    </div>
                    <button
                        onClick={() => stage !== "working" && onClose?.()}
                        className="h-9 w-9 flex items-center justify-center rounded-xl hover:bg-muted disabled:opacity-50"
                        disabled={stage === "working"}
                        aria-label="Close"
                        data-testid="pin-sheet-close"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="px-5 pt-5">
                    {stage === "working" ? (
                        <div className="py-8 flex flex-col items-center" data-testid="pin-sheet-working">
                            <Loader2 size={26} className="text-primary animate-spin" />
                            <p className="mt-3 text-[13px] font-medium">Working…</p>
                        </div>
                    ) : stage === "otp" ? (
                        <>
                            <label className="block">
                                <span className="block text-[11.5px] font-medium text-muted-foreground mb-1 ml-1">
                                    OTP
                                </span>
                                <span className="flex items-center h-12 px-4 rounded-xl border border-border bg-secondary focus-within:bg-background focus-within:border-primary transition-colors">
                                    <KeyRound size={14} className="text-muted-foreground mr-2" />
                                    <input
                                        data-testid="pin-otp-input"
                                        inputMode="numeric"
                                        maxLength={6}
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        className="flex-1 bg-transparent outline-none text-[15px] tracking-widest"
                                        placeholder="000000"
                                    />
                                </span>
                            </label>
                            {err && <p className="mt-2 text-[12px] text-destructive" data-testid="pin-sheet-error">{err}</p>}
                        </>
                    ) : (
                        <>
                            <Pad value={value} onChange={setValue} length={PIN_LEN} testId="pin-sheet-pad" />
                            {err && <p className="mt-3 text-center text-[12px] text-destructive" data-testid="pin-sheet-error">{err}</p>}
                        </>
                    )}
                </div>
                <div className="px-5 pt-4">
                    <button
                        type="button"
                        data-testid="pin-sheet-submit"
                        onClick={submit}
                        disabled={!canSubmit}
                        className="w-full py-3.5 rounded-2xl text-primary-foreground font-medium text-[14.5px] active:scale-[0.98] transition-transform disabled:opacity-50"
                        style={{
                            background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                            boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                        }}
                    >
                        {mode === "verify" ? "Confirm" :
                         (stage === "entry" || stage === "newPin" || stage === "otp") ? "Continue" : "Save PIN"}
                    </button>
                    {showForgot && (
                        <button
                            type="button"
                            data-testid="pin-sheet-forgot"
                            onClick={() => onClose?.({ forgot: true })}
                            className="w-full mt-2 py-2 text-[12.5px] font-medium text-primary hover:underline"
                        >
                            Forgot PIN?
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PinSheet;
